import { useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { Link } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import TableContainer from "components/common/TableContainer";
import { DateOnly } from "components/common/Date";
import { formatCurrency } from "utils/currency";
import { getPaymentExpectations } from "features/PaymentExpectations/services/PaymentExpectationService";
import type { PaymentExpectation } from "features/PaymentExpectations/types/payment-expectation.types";
import moment from "moment";

type PeriodKey = "today" | "7d" | "1m" | "1y";

const PERIOD_OPTIONS: { key: PeriodKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "7d", label: "This week" },
  { key: "1m", label: "This month" },
  { key: "1y", label: "This year" },
];

function getPeriodRange(key: PeriodKey): {
  expectedDateFrom: string;
  expectedDateTo: string;
} {
  const today = moment().format("YYYY-MM-DD");
  switch (key) {
    case "today":
      return { expectedDateFrom: today, expectedDateTo: today };
    case "7d":
      return {
        expectedDateFrom: today,
        expectedDateTo: moment().add(7, "days").format("YYYY-MM-DD"),
      };
    case "1m":
      return {
        expectedDateFrom: moment().startOf("month").format("YYYY-MM-DD"),
        expectedDateTo: moment().endOf("month").format("YYYY-MM-DD"),
      };
    case "1y":
      return {
        expectedDateFrom: moment().startOf("year").format("YYYY-MM-DD"),
        expectedDateTo: moment().endOf("year").format("YYYY-MM-DD"),
      };
  }
}

const columns: ColumnDef<PaymentExpectation>[] = [
  {
    header: "Reference",
    id: "code",
    enableColumnFilter: false,
    cell: ({ row }) => (
      <div>
        <Link
          to={`/finance/payment-expectations/${row.original.id}`}
          className="fw-medium text-body text-decoration-none small"
        >
          {row.original.code}
        </Link>
        {row.original.client?.name ? (
          <div className="text-muted" style={{ fontSize: "11px" }}>
            {row.original.client.name}
          </div>
        ) : null}
      </div>
    ),
  },
  {
    header: "Due date",
    id: "expectedDate",
    enableColumnFilter: false,
    cell: ({ row }) => (
      <span className="small text-muted">
        {row.original.expectedDate ? (
          <DateOnly date={row.original.expectedDate} />
        ) : (
          "—"
        )}
      </span>
    ),
  },
  {
    header: "Balance",
    id: "balance",
    enableColumnFilter: false,
    cell: ({ row }) => {
      const balance = Math.max(
        0,
        Number(row.original.expectedAmount) -
          Number(row.original.receivedAmount),
      );
      return <span className="small fw-medium">{formatCurrency(balance)}</span>;
    },
  },
];

export default function PaymentExpectationsDashboardCard() {
  const [period, setPeriod] = useState<PeriodKey>("7d");

  const requestParams = useMemo(() => getPeriodRange(period), [period]);

  const selectedLabel =
    PERIOD_OPTIONS.find((o) => o.key === period)?.label ?? "Filter";

  return (
    <Card>
      <CardHeader className="d-flex align-items-center justify-content-between">
        <div>
          <h5 className="card-title mb-0">Upcoming Collections</h5>
          <p className="text-muted mb-0" style={{ fontSize: "0.75rem" }}>
            Payment Expectations
          </p>
        </div>
        <UncontrolledDropdown>
          <DropdownToggle
            tag="a"
            className="text-reset dropdown-btn"
            role="button"
          >
            <span className="text-muted small me-1">{selectedLabel}</span>
            <i className="mdi mdi-dots-vertical align-middle text-muted fs-18" />
          </DropdownToggle>
          <DropdownMenu end>
            {PERIOD_OPTIONS.map((opt) => (
              <DropdownItem
                key={opt.key}
                active={period === opt.key}
                onClick={() => setPeriod(opt.key)}
              >
                {opt.label}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </UncontrolledDropdown>
      </CardHeader>
      <CardBody>
        <TableContainer
          columns={columns}
          fetchApi={getPaymentExpectations}
          requestParams={requestParams}
          syncPaginationToUrl={false}
          pageSize={10}
          globalSearch={false}
          hidePageSize
          divClass="table-responsive table-card mb-0"
          tableClass="table-nowrap align-middle mb-0"
          theadClass="table-light text-muted"
        />
      </CardBody>
    </Card>
  );
}
